import React, { useEffect, useRef, useState } from "react";
import { Trash, Plus, X, UploadSimple, Check, WarningCircle, PencilSimple } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const CATS = ["decks", "kitchens", "bathrooms", "exterior", "additions", "general"];
const CONCURRENCY = 3;

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("general");
  const [titlePrefix, setTitlePrefix] = useState("");
  const [queue, setQueue] = useState([]); // [{id, file, preview, status: 'pending'|'uploading'|'done'|'error', error?}]
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(null); // currently-edited gallery item
  const [filter, setFilter] = useState("all");
  const inputRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const enriched = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      preview: URL.createObjectURL(file),
      status: "pending",
    }));
    setQueue((prev) => [...prev, ...enriched]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFromQueue = (id) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const updateQueueItem = (id, patch) => {
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const uploadOne = async (item, indexInBatch) => {
    try {
      updateQueueItem(item.id, { status: "uploading" });
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("folder", "gallery");
      const upRes = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const title = titlePrefix ? `${titlePrefix} ${indexInBatch + 1}` : "";
      await api.post("/gallery", {
        title,
        caption: "",
        category,
        image_path: upRes.data.path,
      });
      updateQueueItem(item.id, { status: "done" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      updateQueueItem(item.id, { status: "error", error: msg });
    }
  };

  const runBatch = async () => {
    const pending = queue.filter((q) => q.status === "pending");
    if (pending.length === 0) return toast.error("Pick some images first");
    setRunning(true);

    let i = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, async () => {
      while (i < pending.length) {
        const myIndex = i++;
        await uploadOne(pending[myIndex], myIndex);
      }
    });
    await Promise.all(workers);

    setRunning(false);
    await load();

    // Auto-close if everything succeeded
    setTimeout(() => {
      setQueue((prev) => {
        const allDone = prev.every((q) => q.status === "done");
        if (allDone) {
          // Revoke previews + close
          prev.forEach((q) => URL.revokeObjectURL(q.preview));
          setOpen(false);
          setTitlePrefix("");
          toast.success(`Uploaded ${prev.length} image${prev.length === 1 ? "" : "s"}`);
          return [];
        }
        const successes = prev.filter((q) => q.status === "done").length;
        const failures = prev.filter((q) => q.status === "error").length;
        toast.success(`Uploaded ${successes}, ${failures} failed — review below`);
        return prev;
      });
    }, 300);
  };

  const closeModal = () => {
    queue.forEach((q) => URL.revokeObjectURL(q.preview));
    setQueue([]);
    setOpen(false);
    setTitlePrefix("");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/gallery/${id}`);
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/gallery/${editing.id}`, {
        title: editing.title || "",
        caption: editing.caption || "",
        category: editing.category || "general",
        image_path: editing.image_path,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);

  const pendingCount = queue.filter((q) => q.status === "pending").length;
  const doneCount = queue.filter((q) => q.status === "done").length;
  const errorCount = queue.filter((q) => q.status === "error").length;

  return (
    <div className="p-8 md:p-10" data-testid="admin-gallery">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Media</div>
          <h1 className="font-display text-5xl uppercase text-white leading-none">Gallery</h1>
          <p className="text-white/60 mt-2 text-sm">Upload project photos. They show up on /gallery immediately.</p>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="new-image-btn"><Plus size={18} weight="bold" /> Upload Images</button>
      </div>

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-16 text-center text-white/50">No images yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-gallery-filters">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${filter === "all" ? "bg-atc-red text-white border-atc-red" : "bg-transparent text-white/70 border-[#333] hover:border-atc-red"}`}
              data-testid="filter-all"
            >
              All <span className="ml-1 opacity-60">{items.length}</span>
            </button>
            {CATS.map((c) => {
              const count = items.filter((i) => i.category === c).length;
              if (count === 0) return null;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${filter === c ? "bg-atc-red text-white border-atc-red" : "bg-transparent text-white/70 border-[#333] hover:border-atc-red"}`}
                  data-testid={`filter-${c}`}
                >
                  {c} <span className="ml-1 opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {visible.map((i) => (
              <div key={i.id} className="admin-card p-3 group relative" data-testid={`gallery-row-${i.id}`}>
                <div className="aspect-square bg-atc-black overflow-hidden">
                  <img src={fileUrl(i.image_path)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="mt-3">
                  <div className="font-heading uppercase text-xs text-white truncate">{i.title || "Untitled"}</div>
                  <div className="font-mono text-[10px] text-atc-red uppercase tracking-widest mt-1">{i.category}</div>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing({ ...i })} className="bg-atc-black/80 text-white p-1.5 hover:bg-atc-red" data-testid={`edit-image-${i.id}`}>
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button onClick={() => del(i.id)} className="bg-atc-red text-white p-1.5" data-testid={`delete-image-${i.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-atc-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="upload-modal">
          <div className="bg-[#1f1f1f] border-2 border-atc-red max-w-3xl w-full p-8 my-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-3xl uppercase text-white">Bulk upload</h2>
                <p className="text-white/50 text-xs font-mono uppercase tracking-widest mt-1">Drop or pick multiple images at once</p>
              </div>
              <button onClick={closeModal} className="text-white/60 hover:text-atc-red" data-testid="close-upload-modal"><X size={24} weight="bold" /></button>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="admin-label">Category (applies to all)</div>
                  <select className="admin-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="bulk-category">
                    {CATS.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <div>
                  <div className="admin-label">Title prefix (optional)</div>
                  <input
                    className="admin-input"
                    value={titlePrefix}
                    onChange={(e) => setTitlePrefix(e.target.value)}
                    placeholder='e.g., "Deck Project"'
                    data-testid="bulk-title-prefix"
                  />
                </div>
              </div>

              <div
                className="border-2 border-dashed border-[#333] hover:border-atc-red p-8 text-center cursor-pointer transition-colors"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-atc-red"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-atc-red"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-atc-red");
                  const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
                  if (files.length === 0) return;
                  const enriched = files.map((file) => ({
                    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    file,
                    preview: URL.createObjectURL(file),
                    status: "pending",
                  }));
                  setQueue((prev) => [...prev, ...enriched]);
                }}
                data-testid="upload-dropzone"
              >
                <UploadSimple size={32} weight="bold" className="text-atc-red mx-auto mb-3" />
                <div className="font-heading uppercase tracking-widest text-sm text-white">Click or drop images here</div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mt-2">jpg · png · webp · gif · 15mb max each</div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onPickFiles}
                  data-testid="bulk-file-input"
                />
              </div>

              {queue.length > 0 && (
                <div data-testid="upload-queue">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-heading uppercase tracking-widest text-xs text-white/70">
                      Queue · {queue.length} file{queue.length === 1 ? "" : "s"}
                      {(doneCount > 0 || errorCount > 0) && (
                        <span className="ml-2 text-white/40 normal-case font-mono">
                          ({doneCount} done{errorCount > 0 ? `, ${errorCount} failed` : ""})
                        </span>
                      )}
                    </div>
                    {!running && (
                      <button
                        onClick={() => {
                          queue.forEach((q) => URL.revokeObjectURL(q.preview));
                          setQueue([]);
                        }}
                        className="text-white/40 hover:text-atc-red font-heading uppercase tracking-widest text-xs"
                        data-testid="clear-queue"
                      >
                        Clear queue
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                    {queue.map((q) => (
                      <div key={q.id} className="relative aspect-square bg-atc-black border border-[#333] overflow-hidden" data-testid={`queue-item-${q.id}`}>
                        <img src={q.preview} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-atc-black/60 flex items-center justify-center">
                          {q.status === "pending" && (
                            <span className="font-mono text-[9px] text-white/70 uppercase tracking-widest">Pending</span>
                          )}
                          {q.status === "uploading" && (
                            <div className="font-mono text-[9px] text-white uppercase tracking-widest animate-pulse">Uploading…</div>
                          )}
                          {q.status === "done" && (
                            <div className="text-white"><Check size={28} weight="bold" /></div>
                          )}
                          {q.status === "error" && (
                            <div className="text-atc-red text-center px-1"><WarningCircle size={20} weight="bold" /><div className="font-mono text-[8px] mt-1 leading-tight">{q.error}</div></div>
                          )}
                        </div>
                        {!running && q.status !== "done" && (
                          <button
                            onClick={() => removeFromQueue(q.id)}
                            className="absolute top-1 right-1 bg-atc-black/80 text-white p-1 hover:bg-atc-red"
                            data-testid={`remove-${q.id}`}
                          >
                            <X size={10} weight="bold" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-[#2a2a2a]">
                <button
                  onClick={runBatch}
                  disabled={running || pendingCount === 0}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-bulk-upload"
                >
                  <UploadSimple size={18} weight="bold" />
                  {running ? "Uploading…" : `Upload ${pendingCount} image${pendingCount === 1 ? "" : "s"}`}
                </button>
                <button onClick={closeModal} disabled={running} className="btn-ghost-white">
                  {doneCount > 0 ? "Done" : "Cancel"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-atc-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-image-modal">
          <div className="bg-[#1f1f1f] border-2 border-atc-red max-w-lg w-full p-8 my-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl uppercase text-white">Edit image</h2>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-atc-red" data-testid="close-edit-modal"><X size={24} weight="bold" /></button>
            </div>
            <div className="space-y-5">
              <div className="aspect-video bg-atc-black border border-[#333] overflow-hidden">
                <img src={fileUrl(editing.image_path)} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="admin-label">Category</div>
                <select
                  className="admin-input"
                  value={editing.category || "general"}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="edit-category"
                >
                  {CATS.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <div className="admin-label">Title</div>
                <input
                  className="admin-input"
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="Optional"
                  data-testid="edit-title"
                />
              </div>
              <div>
                <div className="admin-label">Caption</div>
                <input
                  className="admin-input"
                  value={editing.caption || ""}
                  onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                  placeholder="Optional"
                  data-testid="edit-caption"
                />
              </div>
              <div className="flex gap-3 pt-4 border-t border-[#2a2a2a]">
                <button onClick={saveEdit} className="btn-primary" data-testid="save-edit-btn">Save</button>
                <button onClick={() => setEditing(null)} className="btn-ghost-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
